import {
  DateFormat,
  NullableDateInput,
  stringifyDate,
} from '@superdispatch/dates';
import {
  CUSTOMER_TYPES,
  PaymentMethod,
  PaymentTerm,
  VEHICLE_TYPES,
} from '@superdispatch/sdk';
import { startCase } from 'es-toolkit/compat';
import { businessHoursRowSchema } from 'shared/data/BusinessHoursDTO';
import { PAYMENT_TERMS_AND_METHODS } from 'shared/helpers/CarrierPaymentTypes';
import { isEmptyRecord } from 'shared/helpers/CommonHelpers';
import { driverCdlVerificationStatus } from 'shared/modules/driver/DriverCdlVerificationStatus';
import { BUSINESS_HOURS_TYPES } from 'shared/utils/businessHoursDisplay';
import { z } from 'zod';

const zodString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

/**
 * Same as {@link zodString}, but casts numbers and booleans to strings, the way
 * `yupString()` does — some endpoints send numeric values for string fields.
 */
const zodStringLike = z.preprocess(
  (value) =>
    typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : value,
  zodString,
);

/** Same as {@link zodString}, but rejects empty values. */
function zodRequiredString(error = 'This field is required') {
  return zodString.refine((value) => value.length > 0, error).prefault('');
}

/** Same as {@link zodString}, but rejects values longer than `max` characters. */
function zodMaxString(max: number) {
  return z
    .string()
    .max(max, `Maximum length is ${max} characters`)
    .nullish()
    .transform((value) => value ?? '');
}

/**
 * Same as {@link zodMaxString}, but additionally rejects values that are not a
 * comma separated list of emails.
 */
function zodMultipleEmails(max: number) {
  return z
    .string()
    .max(max, `Maximum length is ${max} characters`)
    .refine(hasValidEmails, 'Enter valid email(s)')
    .nullish()
    .transform((value) => value ?? '');
}

function hasValidEmails(value: string) {
  if (!value) {
    return true;
  }

  return value
    .trim()
    .split(',')
    .every((email) => z.email().safeParse(email.trim()).success);
}

/**
 * Reusable `never` schema that allows to remove properties from a `z.object`.
 */
const zodOmit = z
  .unknown()
  .transform(() => undefined)
  .optional();

function coerceBoolean(value: unknown) {
  return typeof value === 'string' || typeof value === 'number'
    ? /^(true|1)$/i.test(String(value))
    : value;
}

const zodBoolean = z.preprocess(
  coerceBoolean,
  z.boolean().nullable().default(false).catch(false),
);

/**
 * Same as {@link zodBoolean}, but empty values stay `null` instead of becoming
 * `false` — for tri-state flags where "unknown" is meaningful.
 */
const zodNullableBoolean = z.preprocess(
  coerceBoolean,
  z.boolean().nullable().default(null).catch(null),
);

/** Same as {@link zodBoolean}, but never `null` — for the `required()` flags. */
const zodRequiredBoolean = z.preprocess(
  coerceBoolean,
  z.boolean().default(false).catch(false),
);

/** Same as {@link zodBoolean}, but empty values become `true`. */
const zodBooleanDefaultTrue = z.preprocess(
  coerceBoolean,
  z.boolean().nullable().default(true).catch(true),
);

const zodNumber = z.preprocess(
  // `Number('')` is `0`, which would turn a cleared numeric input into a value.
  (value) => (value == null || value === '' ? null : Number(value)),
  z.number().nullable().default(null).catch(null),
);

const zodRequiredNumber = z.preprocess(
  (value) => (value == null ? value : Number(value)),
  z.number().default(0).catch(0),
);

function zodDateString(format: DateFormat) {
  return z.preprocess(
    (value) => stringifyDate(value as NullableDateInput, { format }),
    z.string().nullable().default(null),
  );
}

/** Same as {@link zodDateString}, but rejects empty values. */
function zodRequiredDateString(
  format: DateFormat,
  error = 'This field is required',
) {
  return z
    .preprocess(
      (value) => stringifyDate(value as NullableDateInput, { format }),
      z.string(error),
    )
    .prefault(null);
}

export const LOAD_STAGES = [
  'active',
  'new',
  'in_terminal',
  'assigned',
  'picked_up',
  'delivered',
  'billed',
  'paid',
  'archived',
  'deleted',
  'aging',
  'delayed-pickups',
  'delayed-deliveries',
  'to-pickup-today',
  'to-deliver-today',
  'payable',
  'receivable',
  'receivable/past-due',
  'revenue',
] as const;
export type LoadStage = (typeof LOAD_STAGES)[number];
export function formatLoadStage(stage: LoadStage): string {
  return startCase(stage);
}
export function isLoadStageComplete(stage: LoadStage, compare: LoadStage) {
  return LOAD_STAGES.indexOf(stage) >= LOAD_STAGES.indexOf(compare);
}

const vehicleStatusOrder = { ready: 1, not_ready: 2, canceled: 3 };

export function sortVehiclesByStatus(vehicles: LoadVehicleDTO[]) {
  return vehicles.sort((a, b) => {
    const statusA = a.status == null ? 'ready' : a.status;
    const statusB = b.status == null ? 'ready' : b.status;
    return vehicleStatusOrder[statusA] - vehicleStatusOrder[statusB];
  });
}

//
// LoadVehicleDTO
//

export type LoadVehicleDTO = z.infer<typeof loadVehicleSchema>;
export const loadVehicleSchema = z.object({
  guid: zodString,
  vin: zodString,
  make: zodString,
  model: zodString,
  year: zodNumber,
  color: zodString,
  price: zodNumber,
  type: z.enum(VEHICLE_TYPES).catch('other'),
  lot_number: zodString,
  is_edi: zodBoolean,
  is_inoperable: zodBoolean,
  requires_enclosed_trailer: zodBoolean,
  curb_weight: zodNumber,
  height: zodNumber,
  length: zodNumber,
  width: zodNumber,
  status: z
    .enum(['not_ready', 'ready', 'canceled'])
    .nullable()
    .catch(null)
    .default(null),
});

//
// LoadLegDTO
//

export function isEmptyLoadLegVenue({ contacts, ...venue }: LoadVenueDTO) {
  return isEmptyRecord(venue) && isEmptyRecord(contacts[0]);
}

export type LoadVenueDTO = z.infer<typeof loadVenueSchema>;
export const loadVenueSchema = z.object({
  name: zodMaxString(200),
  address: zodMaxString(256),
  city: zodMaxString(100),
  state: zodMaxString(100),
  zip: zodMaxString(20),
  billing_email: zodString,
  business_type: z.enum(CUSTOMER_TYPES).nullable().catch(null).default(null),
  buyer_number: zodMaxString(200),
  contacts: z
    .array(
      z.object({
        name: zodMaxString(200),
        phone: zodMaxString(200),
        phone2: zodMaxString(200),
        email: zodMultipleEmails(300),
      }),
    )
    .prefault([]),
});

const loadLegBusinessHoursShape = {
  business_hours_type: z
    .enum(BUSINESS_HOURS_TYPES)
    .nullable()
    .catch(null)
    .default(null),
  business_hours: z.array(businessHoursRowSchema).prefault([]),
};

//
// LoadExpenseDTO
//

type LoadExpenseType = (typeof LOAD_EXPENSE_TYPES)[number];
export const LOAD_EXPENSE_TYPES = [
  'pulling_fee',
  'storage_fee',
  'late_fee',
  'dry_run_fee',
  'tax',
  'fuel',
  'toll',
  'other',
] as const;
export function formatLoadExpenseType(type: LoadExpenseType) {
  switch (type) {
    case 'pulling_fee':
      return 'Pulling Fee';
    case 'storage_fee':
      return 'Storage Fee';
    case 'late_fee':
      return 'Late Fee';
    case 'dry_run_fee':
      return 'Dry Run Fee';
    case 'tax':
      return 'Tax';
    case 'fuel':
      return 'Fuel';
    case 'toll':
      return 'Toll';
    case 'other':
      return 'Other';
  }
}
export type LoadExpenseDTO = z.infer<typeof loadExpenseSchema>;
const loadExpensePriceSchema = z
  .preprocess(
    (value) => (value == null ? value : Number(value)),
    z.number('Enter price'),
  )
  .prefault(null);

export const loadExpenseSchema = z.object({
  guid: zodString,
  name: zodMaxString(50),
  type: z.enum(LOAD_EXPENSE_TYPES).catch('other'),
  price: loadExpensePriceSchema,
  date: zodRequiredDateString('DateTimeISO'),
  receipt_url: zodString,
  show_on_invoice: zodBoolean,
  deduct_from_driver_pay: zodBoolean,
});

//
// LoadExpenseChargeTo
//
// The backend still stores `show_on_invoice` / `deduct_from_driver_pay` as two
// independent booleans, so a "Charge to" single-select is derived/applied on
// the frontend to keep them mutually exclusive.
export const LOAD_EXPENSE_CHARGE_TO_OPTIONS = [
  'carrier',
  'driver',
  'shipper',
] as const;
export type LoadExpenseChargeTo =
  (typeof LOAD_EXPENSE_CHARGE_TO_OPTIONS)[number];

interface LoadExpenseChargeToFlags {
  showOnInvoice?: boolean | null;
  deductFromDriverPay?: boolean | null;
}

export function getLoadExpenseChargeTo({
  showOnInvoice,
  deductFromDriverPay,
}: LoadExpenseChargeToFlags): LoadExpenseChargeTo | undefined {
  // Legacy data can have both flags set at once — that's the double-recovery
  // bug we don't want to silently resolve, so we leave it unset instead.
  if (showOnInvoice && deductFromDriverPay) {
    return undefined;
  }
  if (deductFromDriverPay) {
    return 'driver';
  }
  if (showOnInvoice) {
    return 'shipper';
  }
  return 'carrier';
}

export function getLoadExpenseChargeToFlags(chargeTo: LoadExpenseChargeTo): {
  showOnInvoice: boolean;
  deductFromDriverPay: boolean;
} {
  return {
    showOnInvoice: chargeTo === 'shipper',
    deductFromDriverPay: chargeTo === 'driver',
  };
}

export function formatLoadExpenseChargeTo(chargeTo: LoadExpenseChargeTo) {
  switch (chargeTo) {
    case 'carrier':
      return 'Carrier';
    case 'driver':
      return 'Driver';
    case 'shipper':
      return 'Shipper';
  }
}

export const loadPaymentDetailsSchema = z
  .object({
    payment_method: z
      .custom<PaymentMethod>()
      .nullish()
      // Backend does not support empty strings
      .transform((value) => value || null),
    payment_terms: z
      .custom<PaymentTerm | 'quickpay'>()
      .nullish()
      .transform((value) =>
        // Backend may return both 'quickpay' and 'quick_pay', and it does not
        // support empty strings
        value === 'quickpay' ? 'quick_pay' : value || null,
      ),
  })
  .nullable()
  .prefault({});

//
// LoadPaymentDTO
//

export type LoadPaymentStatus = (typeof LOAD_PAYMENT_STATUSES)[number];
const LOAD_PAYMENT_STATUSES = [
  'processing',
  'received',
  'on_hold',
  'failed',
  'payin_failed',
  'past_due',
  'scheduled',
  'pending',
  'canceled',
] as const;

export type LoadPaymentDTO = z.infer<typeof loadPaymentSchema>;
export const loadPaymentSchema = z.object({
  price: zodNumber,
  original_price: zodNumber,
  status: z.enum(LOAD_PAYMENT_STATUSES).nullable().catch(null).default(null),
  method: z
    .enum(PAYMENT_TERMS_AND_METHODS)
    .nullable()
    .catch(null)
    .default(null),
  original_method: z
    .enum(PAYMENT_TERMS_AND_METHODS)
    .nullable()
    .catch(null)
    .default(null),
  status_details: zodString,
  status_info: zodString,
  status_info_url: zodString,
  notes: zodMaxString(50000),
  broker_fee: zodNumber,
  driver_pay: zodNumber,
  payment_due: zodDateString('DateTimeISO'),
  paid_at: zodDateString('DateTimeISO'),
  receipt_date: zodDateString('DateTimeISO'),
  terms: zodString,
  invoice: z
    .object({
      invoice_id: zodString,
      invoice_notes: zodString,
      // Read-only, and the edit forms submit only the two fields above.
      sent_at: zodDateString('DateTimeISO').optional(),
      errors: z
        .array(z.object({ channel: zodString, message: zodString }))
        .prefault([])
        .optional(),
    })
    .prefault({}),
  paid_amount: zodNumber,
  paid_method: zodNumber,
  payment_details: loadPaymentDetailsSchema,
  payment_reference_number: zodString,
  on_hold_reasons: z.array(zodString).prefault([]),
  custom_on_hold_reason: zodString,
  instant_transfer_available: z.boolean().nullable().catch(null).default(null),
  instant_transfer_fee_percentage: zodNumber,
  instant_transfer_fee_amount: zodNumber,
  instant_transfer_paid_amount: zodNumber,
  instant_transfer_received_date: zodDateString('DateTimeISO'),
  instant_transfer_grace_period_end_date: zodDateString('DateTimeISO'),
  instant_transfer_settlement_status: z
    .enum(['info', 'warning', 'past_due', 'failed'])
    .nullable()
    .catch(null)
    .default(null),
});

export type SubhaulLoadPaymentDTO = z.infer<typeof subhaulLoadPaymentSchema>;
export const subhaulLoadPaymentSchema = z.object({
  customer_payment: z
    .object({
      price: zodStringLike,
      paid_amount: zodStringLike,
      paid_date: zodDateString('DateTimeISO'),
      payment_reference_number: zodString,
      status: z
        .enum(['paid', 'unpaid', 'billed'])
        .default('paid')
        .catch('paid'),
      payment_method: z
        .enum(PAYMENT_TERMS_AND_METHODS)
        .nullable()
        .catch(null)
        .default(null),
      paid_method: z
        .enum(PAYMENT_TERMS_AND_METHODS)
        .nullable()
        .catch(null)
        .default(null),
      payment_terms: zodString,
      payment_notes: zodString,
    })
    .prefault({}),
  subhauler_payment: z
    .object({
      paid_at: zodDateString('DateTimeISO'),
      price: zodStringLike,
      payment_method: z
        .enum(PAYMENT_TERMS_AND_METHODS)
        .nullable()
        .catch(null)
        .default(null),
      payment_terms: zodString,
      status: z.enum(['paid', 'unpaid']).default('paid').catch('paid'),
    })
    .prefault({}),
  invoice: z
    .object({ invoice_id: zodString, invoice_notes: zodString })
    .prefault({}),
});

export type LoadSubhaulerPaymentDTO = z.infer<
  typeof loadSubhaulerPaymentSchema
>;
export const loadSubhaulerPaymentSchema = z.object({
  customer: z.object({
    price: zodNumber,
    payment_method: zodRequiredString(),
    payment_terms: zodRequiredString(),
    payment_notes: zodString,
  }),
  subhauler: z.object({
    price: zodRequiredNumber,
    payment_method: zodRequiredString(),
    payment_terms: zodRequiredString(),
  }),
});

export function mapSubhaulerPaymentFields(
  payload: Partial<SubhaulLoadPaymentDTO>,
): LoadSubhaulerPaymentDTO {
  const body: Partial<LoadSubhaulerPaymentDTO> = {};

  if (payload.customer_payment) {
    const customer = payload.customer_payment;
    body.customer = {
      price: Number(customer.price),
      payment_method: customer.payment_method ?? '',
      payment_terms: customer.payment_terms ?? '',
      payment_notes: customer.payment_notes,
    };
  }

  if (payload.subhauler_payment) {
    const subhauler = payload.subhauler_payment;
    body.subhauler = {
      price: Number(subhauler.price),
      payment_method: subhauler.payment_method ?? '',
      payment_terms: subhauler.payment_terms ?? '',
    };
  }

  return body as LoadSubhaulerPaymentDTO;
}

//
// LoadAttachmentDTO
//
export const ATTACHMENT_FILE_TYPES = ['image', 'pdf', 'any'] as const;
export type AttachmentFileType = (typeof ATTACHMENT_FILE_TYPES)[number];

const FILE_TYPE_PATTERNS = {
  pdf: /\.pdf$/i,
  image: /\.(gif|jpe?g|png|tiff|webp|bmp)$/i,
};

export function parseAttachmentFileType(fileName: string): AttachmentFileType {
  for (const [type, pattern] of Object.entries(FILE_TYPE_PATTERNS)) {
    if (pattern.test(fileName)) {
      return type as AttachmentFileType;
    }
  }

  return 'any';
}

export type LoadAttachmentDTO = z.infer<typeof loadAttachmentSchema>;
//
// LoadAttachmentCreateDTO
//

export type LoadAttachmentCreateDTO = z.infer<
  typeof loadAttachmentCreateSchema
>;
//
// LoadAttachmentEditDTO
//

const loadAttachmentSchema = z.object({
  created_by: z
    .enum(['dispatcher', 'driver', 'broker', ''])
    .nullable()
    .catch(null)
    .default(null),
  guid: zodString,
  name: zodString,
  file_type: z.enum(ATTACHMENT_FILE_TYPES).catch('any'),
  file_url: zodRequiredString('Wait for upload(s) to finish successfully'),
  is_created_by_broker: zodBoolean,
  is_visible_for_driver: zodBoolean,
  is_location_protected: zodBoolean.optional(),
});

export const loadAttachmentCreateSchema = loadAttachmentSchema.extend({
  guid: zodOmit,
  load_guid: zodString,
});

export const loadAttachmentEditSchema = loadAttachmentSchema.extend({
  load_guid: zodString,
});

//
// LoadDTO
//

export type LoadStatus = (typeof LOAD_STATUSES)[number];
export const LOAD_STATUSES = [
  'new',
  'picked_up',
  'delivered',
  'offered',
  'revoked',
  'accepted',
] as const;
export function formatLoadStatus(
  input: unknown,
  isInTerminal?: boolean | null,
  terminalName?: string,
  shouldShowUnassigned?: boolean,
  isAssigned?: boolean,
): string {
  if (isInTerminal) {
    return `In ${terminalName}`;
  }
  switch (input as LoadStatus) {
    case 'offered':
      return 'Offered';
    case 'accepted':
      return 'Accepted';
    case 'revoked':
      return 'Revoked';
    case 'new':
      if (shouldShowUnassigned) {
        return isAssigned ? 'Assigned' : 'Unassigned';
      }
      return 'New';
    case 'picked_up':
      return 'Picked Up';
    case 'delivered':
      return 'Delivered';
    default:
      return 'Unknown';
  }
}

export type LoadInspectionType = (typeof LOAD_INSPECTION_TYPES)[number];
export const LOAD_INSPECTION_TYPES = ['standard', 'advanced', 'aiag'] as const;
export function formatLoadInspectionType(type: LoadInspectionType) {
  switch (type) {
    case 'standard':
      return 'Standard';
    case 'advanced':
      return 'Advanced';
    case 'aiag':
      return 'AIAG';
  }
}

export type LoadLegDTO = z.infer<typeof loadLegSchema>;
const loadLegSchema = z.object({
  notes: zodString,
  venue: loadVenueSchema.prefault({}),
  actual_date: zodDateString('DateTimeISO'),
  scheduled_date: zodDateString('DateISO'),
  ...loadLegBusinessHoursShape,
});

export type LoadDriverDTO = z.infer<typeof loadDriverSchema>;
const loadDriverSchema = z.object({
  guid: zodString,
  cdl_verification_status: z
    .enum(driverCdlVerificationStatus)
    .nullable()
    .catch(null)
    .default(null),
  verified_at: zodDateString('DateTimeISO'),
  name: zodString,
  phone_number: zodString,
  email: zodString,
  notes: zodString,
  is_role_active: zodBoolean,
  connection_status: z
    .enum(['pending', 'activated', 'declined', 'deactivated'])
    .catch('pending'),
});

export type LoadDTO = z.infer<typeof loadSchema>;
type LoadPickupDTO = LoadDTO['pickup'];
export type LoadInternalNoteDTO = NonNullable<
  LoadDTO['internal_notes']
>[number];
export type LoadVehicleInspectionDTO = NonNullable<
  LoadDTO['inspection_photos']
>[number];
export type LoadTerminalDiagramDTO = LoadDTO['terminals_diagram'];
export type LoadActivityDTO = LoadDTO['activities'];

const SHIPPER_FEATURES = ['restrict_changing_load_id'] as const;
export const isDisabledLoadId = (load: LoadDTO) => {
  return load.features?.includes('restrict_changing_load_id');
};

type LoadLegBusinessHoursKeys = 'business_hours_type' | 'business_hours';

export function isEmptyLoadLeg({
  venue,
  // We skip business hours in isEmpty checks because they are read-only, and
  // the Add Load form does not collect them at all.
  business_hours_type,
  business_hours,
  ...leg
}: Omit<LoadPickupDTO, LoadLegBusinessHoursKeys> &
  Partial<Pick<LoadPickupDTO, LoadLegBusinessHoursKeys>>) {
  return isEmptyRecord(leg) && isEmptyLoadLegVenue(venue);
}

export function isLoadCanceled(load: LoadDTO) {
  return (
    load.is_canceled_by === 'prime_carrier' ||
    load.is_canceled_by === 'broker' ||
    load.is_canceled_by === 'dispatcher' ||
    load.is_canceled_by === 'driver' ||
    load.is_canceled_by === 'api_user' ||
    load.is_canceled_by_broker === true // TODO: Deleted the `load.is_canceled_by`_broker constraint once the backend fix the issue
  );
}

export type PhotoDTO = z.infer<typeof photoSchema>;
const photoDamagesSchema = z
  .array(z.object({ code: zodString, note: zodString }))
  .prefault([]);

export const photoSchema = z.object({
  id: zodString,
  url: zodString,
  thumbnail_url: zodString,
  type: zodString,
  is_vehicle_detected: zodNullableBoolean,
  damages: photoDamagesSchema,
});

export type VideoDTO = z.infer<typeof videoSchema>;
const videoSchema = z.object({
  guid: zodString,
  url: zodString,
  taken_at: zodString,
  thumbnail_url: zodString,
  latitude: zodNumber,
  longitude: zodNumber,
  type: zodString,
  duration_in_seconds: zodNumber,
});

export type AIAGInspectionDamageDTO = z.infer<
  typeof aiagInspectionDamageSchema
>;
const aiagInspectionCodeSchema = z
  .object({ code: zodString, description: zodString })
  .prefault({});

const aiagInspectionDamageSchema = z.object({
  area: aiagInspectionCodeSchema,
  severity: aiagInspectionCodeSchema,
  type: aiagInspectionCodeSchema,
  photos: z
    .array(
      z.object({
        original_photo_url: zodString,
        photo_thumbnail_url: zodString,
      }),
    )
    .prefault([]),
});

export type LooseItemsDTO = z.infer<typeof looseItemsSchema>;
const inspectionStepSchema = z.enum(['pickup', 'delivery']).catch('pickup');

const looseItemsSchema = z.object({
  odometer: zodString,
  type: inspectionStepSchema,
  navigation_disk_present: zodBoolean,
  spare_tire_present: zodBoolean,
  cargo_cover_present: zodBoolean,
  plugin_charger_cable_present: zodBoolean,
  windscreen_present: zodBoolean,
  radio_present: zodBoolean,
  manuals_present: zodBoolean,
  headphones_present: zodBoolean,
  all_glasses_ok: zodBoolean,
  is_drivable: zodBoolean,
  title_present: zodBoolean,
  headrests_count: zodNumber,
  remotes_count: zodNumber,
  valet_keys_count: zodNumber,
});

const aiagInspectionSchema = z.object({
  damages: z.array(aiagInspectionDamageSchema).prefault([]),
  step: inspectionStepSchema,
});

export type InspectionPhotoDTO = z.infer<typeof inspectionPhotoSchema>;
const inspectionPhotoSchema = z.object({
  guid: zodString,
  rendered_photo: zodString,
  rendered_photo_thumbnail: zodString,
  taken_at: zodDateString('DateTimeISO'),
  type: zodString,
  is_vehicle_detected: zodNullableBoolean,
  damages: photoDamagesSchema,
  is_video: zodBoolean.optional(),
  duration_in_seconds: zodNumber.optional(),
  default_image: zodBoolean.optional(),
});

const inspectionSchema = z.object({
  aiag_inspections: z.array(aiagInspectionSchema).prefault([]),
  guid: zodString,
  make: zodString,
  model: zodString,
  videos: z.array(videoSchema).prefault([]),
  photos: z.array(inspectionPhotoSchema).prefault([]),
  vin: zodString,
  year: zodNumber,
  additional_inspections: z.array(looseItemsSchema).prefault([]),
  pickup_inspection_notes: zodString,
  delivery_inspection_notes: zodString,
});

const loadTerminalSchema = z.object({
  guid: zodString,
  name: zodString,
  is_current: zodBoolean,
  is_delivered: zodBoolean,
});

const loadInternalNoteSchema = z.object({
  text: zodString,
  id: zodRequiredNumber,
  created_at: zodDateString('DateTimeISO'),
  author: z.object({ name: zodString }).prefault({}),
});

const loadActivitySchema = z.object({
  action_type: zodString,
  is_changed_by_broker: zodBoolean,
  level: z.enum(['info', 'critical', 'warning']).catch('info'),
  text: zodString,
  timestamp: zodDateString('DateTimeISO'),
  external_user: zodString,
  user: z.object({ name: zodString, email: zodString }).nullable().prefault({}),
  subtext: z
    .object({
      text: zodString,
      hover_text: zodString,
      away_from_location_in_miles: zodNumber,
    })
    .prefault({}),
});

export const loadSchema = z.object({
  guid: zodString,
  number: zodString,
  load_id: zodString,
  internal_load_id: zodMaxString(25),
  payment_details: loadPaymentDetailsSchema,
  instructions: zodString,
  inspection_type: z
    .enum(LOAD_INSPECTION_TYPES)
    .catch(LOAD_INSPECTION_TYPES[0]),
  claims_count: zodNumber,
  is_created_by_broker: zodRequiredBoolean,
  is_canceled_by_broker: zodBoolean,
  is_canceled_by: z
    .enum(['dispatcher', 'driver', 'broker', 'api_user', 'prime_carrier'])
    .nullable()
    .catch(null)
    .default(null),
  is_dispatched_to_carrier: zodRequiredBoolean,
  is_active: zodRequiredBoolean,
  is_edi: zodBoolean,
  is_received_via_subhaul: zodBoolean,
  is_instant_dispatch: zodBoolean,
  is_protected_attachment_accessed_at_pickup: zodBoolean,
  archived: zodRequiredBoolean,
  status: z.enum(LOAD_STATUSES).catch(LOAD_STATUSES[0]),
  current_leg: zodNumber,
  is_pickedup_from_origin: zodBoolean,
  is_delivered_to_destination: zodBoolean,
  latest_internal_note: zodString,
  stage: z.enum(LOAD_STAGES).catch('new'),
  pickup: loadLegSchema.prefault({}),
  delivery: loadLegSchema.prefault({}),
  delayed_days: zodNumber,
  features: z
    .array(z.enum(SHIPPER_FEATURES).nullable().catch(null).default(null))
    .prefault([]),
  can_be_subhauled: zodBoolean,
  can_be_unbuilt: zodBoolean,
  can_bol_be_viewed: zodBoolean,
  can_invoice_be_sent: zodBoolean,
  can_be_revoked: zodBoolean,
  can_offer_be_sent: zodBoolean,
  can_online_bol_be_viewed: zodBoolean,
  can_be_marked_as_paid_by_customer: zodBoolean,
  can_be_marked_as_unpaid_by_customer: zodBoolean,
  can_be_marked_as_paid_to_subhauler: zodBoolean,
  can_be_marked_as_unpaid_to_subhauler: zodBoolean,
  can_edit_pickup_address: zodBooleanDefaultTrue,
  can_add_terminal: zodRequiredBoolean,
  can_be_assigned_to_driver: zodRequiredBoolean,
  can_be_delivered: zodRequiredBoolean,
  can_be_dispatched_to_carrier: zodRequiredBoolean,
  can_be_duplicated: zodRequiredBoolean,
  can_be_edited: zodBoolean,
  can_edit_customer_information: zodBooleanDefaultTrue,
  pickup_driver_address: zodString,
  delivery_driver_address: zodString,
  // `can_payment_be_edited` was added before the api implementation — the
  // default can go once the api starts returning the field.
  can_payment_be_edited: zodBooleanDefaultTrue,
  can_be_marked_as_new: zodBoolean,
  can_be_marked_as_paid: zodRequiredBoolean,
  can_be_marked_as_unpaid: zodBoolean,
  can_be_picked_up: zodRequiredBoolean,
  can_be_split: zodRequiredBoolean,
  can_be_unassigned_from_driver: zodRequiredBoolean,
  can_mark_as_in_terminal: zodRequiredBoolean,
  can_send_invoice: zodRequiredBoolean,
  can_send_bol: zodRequiredBoolean,
  can_view_invoice: zodRequiredBoolean,
  can_view_bol: zodRequiredBoolean,
  is_changed_by_broker: zodBoolean,
  terminals: z.array(loadTerminalSchema).prefault([]),
  terminals_diagram: z
    .array(z.object({ is_active: zodRequiredBoolean, is_complete: zodBoolean }))
    .prefault([]),
  // The api returns more trip fields than the two we read, and Yup kept them
  // with `.unknown()` — `looseObject` is the Zod counterpart.
  trip: z
    .looseObject({ name: zodString, guid: zodString })
    .nullable()
    .prefault({}),
  driver: loadDriverSchema.nullable().default(null),
  vehicles: z.array(loadVehicleSchema).prefault([]),
  expenses: z.array(loadExpenseSchema).prefault([]),
  customer: z
    .object({
      shipper_profile_url: zodString,
      customer_profile_url: zodString,
      venue: loadVenueSchema.prefault({}),
    })
    .prefault({}),
  url_in_stms: zodString,
  should_request_dispatch_to_carrier: zodRequiredBoolean,
  payments: z.array(loadPaymentSchema).prefault([]),
  internal_notes: z.array(loadInternalNoteSchema).prefault([]),
  attachments: z.array(loadAttachmentSchema).prefault([]),
  activities: z.array(loadActivitySchema).prefault([]),
  signatures: z
    .object({
      delivery_driver_signature: zodString,
      delivery_signature_address: zodString,
      delivery_touchless_signature_sms_sent_at: zodDateString('DateTimeISO'),
      pickup_driver_signature: zodString,
      pickup_signature_address: zodString,
      pickup_touchless_signature_sms_sent_at: zodDateString('DateTimeISO'),
    })
    .prefault({}),
  inspection_info: z.array(inspectionSchema).prefault([]),
  inspection_photos: z.array(inspectionSchema).prefault([]),
  undetected_vehicle_count: zodNumber,
  online_bol_url: zodString,
  subhauler: z
    .object({
      guid: zodString,
      name: zodString,
      contact_name: zodString,
      email: zodString,
      phone_number: zodString,
    })
    .optional(),
  subhauler_payment: z
    .object({
      paid_at: zodDateString('DateTimeISO'),
      price: zodStringLike,
      payment_method: z
        .enum(PAYMENT_TERMS_AND_METHODS)
        .nullable()
        .catch(null)
        .default(null),
      payment_terms: zodString,
      status: z
        .enum(['paid', 'unpaid', 'billed'])
        .catch('paid')
        .default('paid'),
    })
    .optional(),
  subhauler_invoice: z
    .object({ received_at: zodDateString('DateTimeISO') })
    .optional(),
  customer_invoice: z
    .object({
      number: zodString,
      sent_at: zodDateString('DateTimeISO'),
      invoice_notes: zodString,
    })
    .optional(),
  customer_payment: z
    .object({
      price: zodStringLike,
      paid_amount: zodStringLike,
      paid_date: zodDateString('DateTimeISO'),
      payment_reference_number: zodString,
      status: z
        .enum(['paid', 'unpaid', 'billed'])
        .catch('paid')
        .default('paid'),
      payment_method: z
        .enum(PAYMENT_TERMS_AND_METHODS)
        .nullable()
        .catch(null)
        .default(null),
      paid_method: z
        .enum(PAYMENT_TERMS_AND_METHODS)
        .nullable()
        .catch(null)
        .default(null),
      payment_terms: zodString,
      payment_notes: zodString,
    })
    .optional(),
  photo_sync_status: z
    .object({
      pickup: z.object({ is_driver_uploading_photo: zodBoolean }).prefault({}),
      delivery: z.object({ is_driver_uploading_photo: zodBoolean }).optional(),
    })
    .optional(),
});

export type AddLoadDTO = z.infer<typeof addLoadSchema>;
const addLoadLegSchema = z.object({
  notes: zodString,
  venue: loadVenueSchema.prefault({}),
  actual_date: zodDateString('DateTimeISO'),
  scheduled_date: zodDateString('DateISO'),
});

/** The Add Load form collects a subset of {@link loadVehicleSchema}. */
const addLoadVehicleSchema = z.object({
  vin: zodString,
  make: zodString,
  model: zodString,
  year: zodNumber,
  color: zodString,
  price: zodNumber,
  type: z.enum(VEHICLE_TYPES).catch('other').default('other'),
  lot_number: zodString,
  is_inoperable: zodBoolean,
  requires_enclosed_trailer: zodBoolean,
});

export const addLoadSchema = z.object({
  number: zodString,
  internal_load_id: zodMaxString(25),
  instructions: zodString,
  inspection_type: z
    .enum(LOAD_INSPECTION_TYPES)
    .catch(LOAD_INSPECTION_TYPES[0])
    .default(LOAD_INSPECTION_TYPES[0]),
  save_pickup_contact: zodBoolean,
  save_delivery_contact: zodBoolean,
  save_customer_contact: zodBoolean,
  payment_details: loadPaymentDetailsSchema,
  pickup: addLoadLegSchema.prefault({}),
  delivery: addLoadLegSchema.prefault({}),
  customer: z
    .object({
      shipper_profile_url: zodString,
      venue: loadVenueSchema.prefault({}),
    })
    .prefault({}),
  payments: z.array(loadPaymentSchema).prefault([]),
  vehicles: z.array(addLoadVehicleSchema).prefault([]),
  attachments: z
    .array(loadAttachmentSchema.extend({ guid: zodOmit }))
    .prefault([]),
});

export type LoadCountsDTO = z.infer<typeof loadCountsSchema>;
export const loadCountsSchema = z.object({ count: zodNumber });
export type LoadStagesCounts = Partial<Record<LoadStage, LoadCountsDTO>>;

export type LoadUpdateTerminalDTO = z.infer<typeof loadUpdateTerminalSchema>;
export const loadUpdateTerminalSchema = z.object({
  terminals_diagram: z
    .array(
      z.object({
        is_active: z.preprocess(
          coerceBoolean,
          z.boolean().default(false).catch(false),
        ),
        is_complete: zodBoolean,
      }),
    )
    .prefault([]),
});

export type LoadMarkAsPaidDTO = z.infer<typeof loadMarkAsPaidSchema>;
export const loadMarkAsPaidSchema = z.object({
  paid_amount: zodNumber,
  paid_method: zodNumber,
  receipt_date: zodDateString('DateTimeISO'),
  payment_notes: zodString,
  payment_reference_number: zodString,
});

const LOAD_CANCEL_REASONS = [
  'unit_not_ready',
  'unknown_inoperable_vehicle',
  'cannot_meet_dates',
  'low_price',
  'changed_route',
] as const;

export const cancelLoadSchema = z.object({
  cancel_reason_type: z
    .enum(LOAD_CANCEL_REASONS)
    .or(z.literal(''))
    .catch('')
    .default(''),
  cancel_reason: zodMaxString(512),
});

export const OFFER_DECLINE_REASONS = [
  'low_price',
  'lack_of_capacity',
  'inconvenient_dates',
] as const;

export type AttachmentDTO = z.infer<typeof attachmentSchema>;
const attachmentSchema = z.object({
  id: zodNumber,
  guid: zodString,
  file: zodString,
  file_type: zodString,
  name: zodString,
});

const orderVehicleSchema = z.object({
  guid: zodString,
  vin: zodString,
  make: zodString,
  model: zodString,
  year: zodNumber,
});

export type OrdersDTO = z.infer<typeof orderLoadSchema>;
export const orderLoadSchema = z.object({
  id: zodString,
  guid: zodString,
  number: zodString,
  internal_load_id: zodString,
  url: zodString,
  status: zodNumber,
  status_display: zodString,
  archived: zodBoolean,
  is_paid: zodBoolean,
  price: zodStringLike,
  invoice_id: zodString,
  invoice_date: zodString,
  invoice_sent_date: zodString,
  invoice_price: zodStringLike,
  vehicles: z.array(orderVehicleSchema).prefault([]),
  attachments: z.array(attachmentSchema).prefault([]),
  pickup_name: zodString,
  pickup_contact: zodString,
  pickup_address: zodString,
  pickup_city: zodString,
  pickup_state: zodString,
  pickup_zip: zodString,
  pickup_date: zodString,
  pickedup_date: zodString,
  delivery_name: zodString,
  delivery_contact: zodString,
  delivery_address: zodString,
  delivery_city: zodString,
  delivery_state: zodString,
  delivery_zip: zodString,
  delivery_date: zodString,
  delivered_date: zodString,
  broker_name: zodString,
  payment: z.object({ method: zodString, terms: zodString }).prefault({}),
});

export type CustomerDTO = z.infer<typeof customerSchema>;
const customerSchema = z.object({
  name: zodString,
  email: zodString,
  fax: zodString,
});

export type AccountInfoDTO = z.infer<typeof accountInfoSchema>;
export const accountInfoSchema = z.object({
  is_factoring_switched_on: zodBoolean,
  customer: customerSchema.prefault({}),
  invoice_number: zodString,
  invoice_date: zodString,
  qb_online: z
    .object({ connected: zodBoolean, message: zodString })
    .prefault({}),
  qb_desktop: z
    .object({
      connected: zodBoolean,
      is_invoice_sent: zodBoolean,
      message: zodString,
    })
    .prefault({}),
  order: orderLoadSchema.prefault({}),
  attachments: z.array(attachmentSchema).prefault([]),
});

export type CombinedLoadDTO = z.infer<typeof combinedLoadSchema>;
export const combinedLoadSchema = z.object({
  guid: zodString,
  number: zodString,
  status_display: zodString,
  is_paid: zodBoolean,
  invoice_date: zodString,
  vehicles_count: zodNumber,
  pickup_city: zodString,
  pickup_state: zodString,
  pickup_zip: zodString,
  delivery_city: zodString,
  delivery_state: zodString,
  delivery_zip: zodString,
  invoice_price: zodStringLike,
});

export type SubhauledLoadDTO = z.infer<typeof subhauledLoadSchema>;
export const subhauledLoadSchema = z.object({
  carrier_guid: zodString,
  subhauling_price: zodNumber,
  original_price: zodNumber,
  payment_method: zodString,
  payment_terms: zodString,
});

export type LoadPhotoSyncStatusDTO = z.infer<typeof loadPhotoSyncStatusSchema>;
const photoSyncCountsSchema = z
  .object({ total: zodNumber, uploaded: zodNumber, pending: zodNumber })
  .prefault({});

export const loadPhotoSyncStatusSchema = z.object({
  pickup: photoSyncCountsSchema,
  delivery: photoSyncCountsSchema,
});
